import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShieldHalved,
    faBan,
    faExclamationTriangle,
    faChartBar,
    faSpinner,
    faArrowUp,
    faArrowDown,
} from '@fortawesome/free-solid-svg-icons';
import adminSecurityService from '../../../helpers/adminSecurityService';

const SecurityDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);
    const [blockedIPs, setBlockedIPs] = useState([]);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [statsData, ipsData] = await Promise.all([
                adminSecurityService.getSecurityStats({ days: 7 }),
                adminSecurityService.getBlockedIPs(),
            ]);
            setStats(statsData);
            setBlockedIPs(ipsData || []);
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError(err.message || 'Error al cargar datos del dashboard');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl text-gray-400" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex items-start gap-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mt-0.5" />
                    <div>
                        <h5 className="font-semibold text-red-900 mb-1">Error al cargar datos</h5>
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    const totalBlockedIPs = blockedIPs.length;
    const permanentBlocked = blockedIPs.filter((ip) => ip.isPermanent).length;
    const temporaryBlocked = totalBlockedIPs - permanentBlocked;
    const suspiciousClients = stats?.suspiciousClients || 0;
    const totalClients = stats?.totalClients || 0;
    const spamPercentage = totalClients > 0 ? ((suspiciousClients / totalClients) * 100).toFixed(1) : 0;

    return (
        <div className="space-y-6">
            {/* Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total IPs Bloqueadas */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">IPs Bloqueadas</p>
                            <p className="text-3xl font-bold text-gray-900 mt-2">{totalBlockedIPs}</p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                                <span className="bg-red-100 text-red-800 px-2 py-1 rounded">
                                    {permanentBlocked} permanentes
                                </span>
                                <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                                    {temporaryBlocked} temporales
                                </span>
                            </div>
                        </div>
                        <div className="bg-red-100 rounded-full p-3">
                            <FontAwesomeIcon icon={faBan} className="text-red-600 text-2xl" />
                        </div>
                    </div>
                </div>

                {/* Clientes Sospechosos */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Clientes Sospechosos</p>
                            <p className="text-3xl font-bold text-gray-900 mt-2">{suspiciousClients}</p>
                            <p className="text-xs text-gray-500 mt-2">
                                {spamPercentage}% del total ({totalClients} clientes)
                            </p>
                        </div>
                        <div className="bg-yellow-100 rounded-full p-3">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 text-2xl" />
                        </div>
                    </div>
                </div>

                {/* Patrones Detectados */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Patrones Detectados</p>
                            <p className="text-3xl font-bold text-gray-900 mt-2">
                                {stats?.patterns ? Object.keys(stats.patterns).length : 0}
                            </p>
                            <p className="text-xs text-gray-500 mt-2">Últimos 7 días</p>
                        </div>
                        <div className="bg-blue-100 rounded-full p-3">
                            <FontAwesomeIcon icon={faChartBar} className="text-blue-600 text-2xl" />
                        </div>
                    </div>
                </div>

                {/* Estado del Sistema */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Estado del Sistema</p>
                            <p className="text-2xl font-bold text-green-600 mt-2">Activo</p>
                            <p className="text-xs text-gray-500 mt-2">Protección habilitada</p>
                        </div>
                        <div className="bg-green-100 rounded-full p-3">
                            <FontAwesomeIcon icon={faShieldHalved} className="text-green-600 text-2xl" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Top IPs Bloqueadas */}
            {stats?.topBlockedIPs && stats.topBlockedIPs.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="p-6 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Top IPs Bloqueadas</h3>
                        <p className="text-sm text-gray-600 mt-1">IPs con más intentos bloqueados</p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        IP
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Intentos
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Razón
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tipo
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Fecha
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {stats.topBlockedIPs.slice(0, 5).map((ipData, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                                            {ipData.ip}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                {ipData.blockedAttempts || 0}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                                            {ipData.reason}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {ipData.isPermanent ? (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                    Permanente
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                    Temporal
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {ipData.createdAt
                                                ? new Date(ipData.createdAt).toLocaleDateString('es-ES')
                                                : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Patrones de Spam */}
            {stats?.patterns && Object.keys(stats.patterns).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="p-6 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Patrones de Spam Detectados</h3>
                        <p className="text-sm text-gray-600 mt-1">Distribución de patrones en los últimos 7 días</p>
                    </div>
                    <div className="p-6">
                        <div className="space-y-3">
                            {Object.entries(stats.patterns)
                                .sort(([, a], [, b]) => b - a)
                                .map(([pattern, count]) => (
                                    <div key={pattern} className="flex items-center justify-between">
                                        <span className="text-sm text-gray-700">{pattern}</span>
                                        <div className="flex items-center gap-2">
                                            <div className="w-32 bg-gray-200 rounded-full h-2">
                                                <div
                                                    className="bg-red-600 h-2 rounded-full"
                                                    style={{
                                                        width: `${(count / suspiciousClients) * 100}%`,
                                                    }}
                                                />
                                            </div>
                                            <span className="text-sm font-medium text-gray-900 w-12 text-right">
                                                {count}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Gráfico de Actividad Diaria */}
            {stats?.dailyClients && stats.dailyClients.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="p-6 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Actividad Diaria</h3>
                        <p className="text-sm text-gray-600 mt-1">Clientes creados por día (últimos 7 días)</p>
                    </div>
                    <div className="p-6">
                        <div className="flex items-end justify-between gap-2 h-48">
                            {stats.dailyClients.map((day, index) => {
                                const maxCount = Math.max(...stats.dailyClients.map((d) => d.count));
                                const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                                return (
                                    <div key={index} className="flex-1 flex flex-col items-center gap-2">
                                        <div className="w-full flex flex-col items-center justify-end h-full">
                                            <div
                                                className="w-full bg-blue-600 rounded-t transition-all hover:bg-blue-700"
                                                style={{ height: `${height}%` }}
                                                title={`${day.count} clientes`}
                                            />
                                        </div>
                                        <span className="text-xs text-gray-600">
                                            {new Date(day._id).toLocaleDateString('es-ES', {
                                                day: '2-digit',
                                                month: '2-digit',
                                            })}
                                        </span>
                                        <span className="text-xs font-medium text-gray-900">{day.count}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SecurityDashboard;

